import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography
} from "@mui/material";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullseye,
  faFileLines,
  faLeaf,
  faReceipt
} from "@fortawesome/free-solid-svg-icons";

import * as Sentry from "@sentry/react";

import {
  LoadingButton,
  Select,
  TextField as AtomTextField
} from "@aclymatepackages/atoms";
import {
  formatDate,
  formatDecimal,
  formatDollars
} from "@aclymatepackages/formatters";
import { emailRegExpTest } from "@aclymatepackages/reg-exp";

import ScoutFab from "../scout/ScoutFab";
import SettingsTabs from "./settings/SettingsTabs";
import {
  DEFAULT_SETTINGS_TAB,
  isValidSettingsTab,
  parseTabParam
} from "./settings/helpers";
import { filterEmissionsForYear, sumTons, toMillis } from "./dashboardHelpers";

import Card from "../../atoms/mui/Card";
import GrayPaper from "../../atoms/GrayPaper";
import Link from "../../atoms/mui/Link";
import FileUploadDropzone from "../../atoms/FileUploadDropzone";
import UserManagementPanel, {
  ROLE_OPTIONS
} from "../../modules/UserManagementPanel";

import {
  deleteFileFromStorage,
  uploadFileToStorage,
  useAccountData,
  useAuth,
  useCachedDisplayData,
  useCachedFirebaseCrud,
  useCollectionDataListener
} from "../../../helpers/firebase";
import { getAccountId } from "../../../helpers/accountHelpers";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import usePermissions from "../../../helpers/hooks/usePermissions";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import useExplorerLayoutContext from "../../../helpers/contexts/explorerLayout";
import { analyticsTrack } from "../../../helpers/analytics";

const LoadingPanel = () => (
  <Box display="flex" justifyContent="center" p={4}>
    <CircularProgress />
  </Box>
);

const GoalsTab = ({
  sustainabilityGoals,
  isAccountDataLoading,
  updateAccountData,
  accountId,
  isAdmin
}) => {
  const [localGoals, setLocalGoals] = useState(sustainabilityGoals);
  const [isSaving, setIsSaving] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (hasInitialized) {
      return;
    }
    if (isAccountDataLoading) {
      return;
    }
    setLocalGoals(sustainabilityGoals);
    setHasInitialized(true);
  }, [hasInitialized, isAccountDataLoading, sustainabilityGoals]);

  const isDirty = localGoals !== sustainabilityGoals;

  const onSave = async () => {
    setIsSaving(true);
    setSaveError("");
    try {
      await updateAccountData({ sustainabilityGoals: localGoals });
      analyticsTrack("settings_goals_saved", {
        accountId,
        hasNonEmptyValue: !!localGoals
      });
      if (localGoals && localGoals !== sustainabilityGoals) {
        analyticsTrack("sustainability_goals_updated", {
          goalsText: localGoals,
          timestamp: Date.now()
        });
      }
    } catch (err) {
      console.error("settings goals save failed", err);
      setSaveError("Couldn't save your goals. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isAccountDataLoading) {
    return <LoadingPanel />;
  }

  return (
    <Card
      title="Sustainability Goals"
      subtitle="Save your top sustainability goals so Scout can tailor its guidance."
      icon={<FontAwesomeIcon icon={faBullseye} />}
    >
      <Box p={2}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <AtomTextField
              value={localGoals}
              setValue={setLocalGoals}
              label="Sustainability Goals"
              multiline
              minRows={3}
              disabled={!isAdmin}
              helperText={
                isAdmin
                  ? "Save your top sustainability goals. Scout references these on this Settings page when you ask for tailored guidance."
                  : "Only admins can edit sustainability goals."
              }
            />
          </Grid>
          {isAdmin && (
            <Grid item>
              <LoadingButton
                label="Save Goals"
                color="secondary"
                onClick={onSave}
                isLoading={isSaving}
                disabled={!isDirty}
              />
            </Grid>
          )}
          {saveError && (
            <Grid item>
              <Typography variant="caption" color="error">
                {saveError}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Card>
  );
};

const DocumentRow = ({ doc, onRemove, isRemoving }) => {
  const { name, uploadedAt, downloadUrl } = doc;
  return (
    <GrayPaper>
      <Box p={2}>
        <Grid container alignItems="center" spacing={2} wrap="nowrap">
          <Grid item xs={12} sm>
            <Typography variant="body2">{name}</Typography>
            {uploadedAt && (
              <Typography variant="caption" color="textSecondary">
                Uploaded {formatDate(uploadedAt)}
              </Typography>
            )}
          </Grid>
          {downloadUrl && (
            <Grid item>
              <Link href={downloadUrl}>
                <Typography variant="body2" color="primary">
                  Download
                </Typography>
              </Link>
            </Grid>
          )}
          <Grid item>
            <IconButton
              aria-label="Remove document"
              size="small"
              onClick={() => onRemove(doc)}
              disabled={isRemoving}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    </GrayPaper>
  );
};

const DocumentsEmptyState = () => (
  <Box display="flex" flexDirection="column" alignItems="center" gap={1} p={3}>
    <Typography variant="subtitle1">
      No historical documents uploaded yet
    </Typography>
    <Typography variant="body2" color="textSecondary" align="center">
      Drop a sustainability report, GHG inventory, or any other document Scout
      should know about.
    </Typography>
  </Box>
);

const DocumentsTab = ({
  historicalDocuments,
  isAccountDataLoading,
  updateAccountData,
  accountId,
  userUid
}) => {
  const [duplicateFile, setDuplicateFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [removingPath, setRemovingPath] = useState("");

  const performUpload = async (file, hasUserConfirmedUpload) => {
    setErrorMessage("");
    const storagePath = `${accountId}/onboarding/historical-documents/${file.name}`;
    try {
      const result = await uploadFileToStorage({
        path: storagePath,
        fileObject: file,
        hasUserConfirmedUpload
      });

      if (result?.isDuplicateFileDetected) {
        setDuplicateFile(file);
        return;
      }

      const newEntry = {
        name: file.name,
        storagePath,
        downloadUrl: result,
        uploadedAt: Date.now(),
        uploadedBy: userUid || ""
      };

      const filteredDocs = (historicalDocuments || []).filter(
        ({ storagePath: existingPath }) => existingPath !== storagePath
      );
      const mergedDocs = [...filteredDocs, newEntry];

      await updateAccountData({ historicalDocuments: mergedDocs });
      analyticsTrack("settings_document_uploaded", {
        accountId,
        filename: file.name,
        fileSize: file.size
      });
      analyticsTrack("historical_document_uploaded", {
        documentCount: mergedDocs.length,
        timestamp: Date.now()
      });
    } catch (err) {
      console.error("historical document upload failed", { storagePath, err });
      setErrorMessage("Upload failed. Please try again.");
    }
  };

  const onConfirmReplace = async () => {
    const file = duplicateFile;
    setDuplicateFile(null);
    if (!file) {
      return;
    }
    await performUpload(file, true);
  };

  const onCancelReplace = () => setDuplicateFile(null);

  const onRemove = async (docToRemove) => {
    const { storagePath } = docToRemove;
    setRemovingPath(storagePath);
    setErrorMessage("");
    const remainingDocs = (historicalDocuments || []).filter(
      ({ storagePath: path }) => path !== storagePath
    );
    try {
      await updateAccountData({ historicalDocuments: remainingDocs });
      analyticsTrack("settings_document_removed", {
        accountId,
        storagePath
      });
      deleteFileFromStorage(storagePath).catch((err) =>
        console.error("storage delete failed for removed document", {
          storagePath,
          err
        })
      );
    } catch (err) {
      console.error("document remove failed", { storagePath, err });
      setErrorMessage("Couldn't remove that document. Please try again.");
    } finally {
      setRemovingPath("");
    }
  };

  if (isAccountDataLoading) {
    return <LoadingPanel />;
  }

  const hasDocuments = (historicalDocuments || []).length > 0;

  return (
    <>
      <Dialog open={!!duplicateFile} onClose={onCancelReplace}>
        <DialogTitle>Replace existing document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A document named <strong>{duplicateFile?.name}</strong> already
            exists. Replace it with the new one?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelReplace}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirmReplace}
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>
      <Card
        title="Historical Documents"
        subtitle="Upload sustainability reports, GHG inventories, or other documents Scout should reference."
        icon={<FontAwesomeIcon icon={faFileLines} />}
      >
        <Box p={2}>
          <Grid container direction="column" spacing={2}>
            {!hasDocuments && (
              <Grid item>
                <DocumentsEmptyState />
              </Grid>
            )}
            {(historicalDocuments || []).map((doc) => (
              <Grid item key={doc.storagePath || doc.name}>
                <DocumentRow
                  doc={doc}
                  onRemove={onRemove}
                  isRemoving={removingPath === doc.storagePath}
                />
              </Grid>
            ))}
            <Grid item>
              <Box sx={{ height: 140 }}>
                <FileUploadDropzone
                  fileName="historical document"
                  onFileUpload={(file) => performUpload(file, false)}
                />
              </Box>
            </Grid>
            {errorMessage && (
              <Grid item>
                <Typography variant="caption" color="error">
                  {errorMessage}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </Card>
    </>
  );
};

const InviteUserDialog = ({
  open,
  onClose,
  vendorId,
  vendorName,
  inviterName
}) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const isEmailValid = emailRegExpTest(email);

  const onSend = async () => {
    setIsSending(true);
    setError("");
    try {
      await fetchOurApi({
        path: "/vendors/invite-explorer-team-member",
        method: "POST",
        data: { vendorId, email, role, inviterName, vendorName },
        callback: (res) => {
          if (res?.error) {
            setError(res.message || "Failed to send invite. Please try again.");
            return;
          }
          setSent(true);
        }
      });
    } catch (err) {
      console.error("invite-explorer-team-member request failed", err);
      setError("Couldn't send the invite. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setRole("admin");
    setSent(false);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Invite a Team Member</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        {sent ? (
          <Typography variant="body2">
            Invite sent to <strong>{email}</strong>. They&apos;ll receive an
            email with a link to join your account.
          </Typography>
        ) : (
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <MuiTextField
                label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size="small"
                error={!!email && !isEmailValid}
              />
            </Grid>
            <Grid item>
              <Select
                label="Role"
                value={role}
                editValue={setRole}
                options={ROLE_OPTIONS.map(({ value, label, description }) => ({
                  value,
                  label: `${label} — ${description}`
                }))}
                size="small"
              />
            </Grid>
            {error && (
              <Grid item>
                <Typography variant="caption" color="error">
                  {error}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{sent ? "Close" : "Cancel"}</Button>
        {!sent && (
          <Button
            variant="contained"
            color="primary"
            onClick={onSend}
            disabled={!isEmailValid || isSending}
          >
            {isSending ? <CircularProgress size={20} /> : "Send Invite"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const JoinRequestRow = ({ request, onResolve, companyId, adminUid }) => {
  const { contactName, contactEmail, buyerName, dateRequested, id } = request;
  const [selectedRole, setSelectedRole] = useState("viewer");
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionError, setActionError] = useState("");

  const handleAction = async (action) => {
    setIsProcessing(true);
    setActionError("");
    try {
      const res = await fetchOurApi({
        path: "/vendors/handle-vendor-join-request",
        method: "POST",
        data: {
          vendorId: companyId,
          requestId: id,
          action,
          adminUid,
          ...(action === "approve" ? { role: selectedRole } : {})
        }
      });
      if (res?.error) {
        console.error("vendor join-request action rejected", {
          id,
          action,
          res
        });
        setActionError("Couldn't update this request. Please try again.");
        return;
      }
      onResolve(id);
    } catch (err) {
      console.error("vendor join-request action failed", { id, action, err });
      setActionError("Couldn't update this request. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <GrayPaper>
      <Box p={2}>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Typography variant="body2">
              <strong>{contactName || contactEmail}</strong> was requested by{" "}
              {buyerName}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {dateRequested ? formatDate(dateRequested) : ""}
            </Typography>
          </Grid>
          <Grid item container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Select
                label="Role if approved"
                value={selectedRole}
                editValue={setSelectedRole}
                options={ROLE_OPTIONS.map(({ value, label, description }) => ({
                  value,
                  label: `${label} — ${description}`
                }))}
                size="small"
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => handleAction("approve")}
                disabled={isProcessing}
              >
                {isProcessing ? <CircularProgress size={16} /> : "Approve"}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleAction("decline")}
                disabled={isProcessing}
              >
                Decline
              </Button>
            </Grid>
          </Grid>
          {actionError && (
            <Grid item>
              <Typography variant="caption" color="error">
                {actionError}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </GrayPaper>
  );
};

const TeamTab = ({
  isAdmin,
  companyId,
  vendorName,
  currentUser,
  currentUserName,
  employees,
  employeesLoading,
  joinRequests,
  joinRequestsLoading,
  updateCollectionDoc
}) => {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resolvedIds, setResolvedIds] = useState([]);
  const { uid: adminUid } = currentUser || {};
  const { activateSnackbar } = useExplorerLayoutContext() || {};

  const pendingRequests = (joinRequests || []).filter(
    ({ id }) => !resolvedIds.includes(id)
  );

  const onResolve = (requestId) =>
    setResolvedIds((prev) => [...prev, requestId]);

  const onRoleChange = async (individualId, newRole) => {
    try {
      await updateCollectionDoc("employees", individualId, { role: newRole });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "explorer-team-role-change" },
        extra: { individualId, newRole, companyId }
      });
      if (activateSnackbar) {
        activateSnackbar({
          severity: "error",
          message:
            "Couldn't update that team member's role — please refresh and try again."
        });
      }
    }
  };

  const deleteOrphanedAuthUser = async (shouldDelete, authUid) => {
    if (!shouldDelete) {
      return { skipped: true };
    }
    return fetchOurApi({
      path: "/users/delete",
      method: "POST",
      data: { uid: authUid }
    }).catch((err) => ({ error: true, err }));
  };

  const onRemove = async ({
    authUid,
    email,
    name,
    id,
    currentLinkedIds = [],
    links = []
  }) => {
    const remainingIds = currentLinkedIds.filter((cid) => cid !== companyId);
    const shouldDeleteAuthUser = remainingIds.length === 0;

    const authDeleteRes = await deleteOrphanedAuthUser(
      shouldDeleteAuthUser,
      authUid
    );
    if (authDeleteRes?.error) {
      Sentry.captureException(authDeleteRes.err, {
        tags: { feature: "explorer-team-remove" },
        extra: { authUid, companyId }
      });
      if (activateSnackbar) {
        activateSnackbar({
          severity: "error",
          message:
            "Couldn't remove that team member — please refresh and try again."
        });
      }
      return;
    }

    fetchOurApi({
      path: "/emails/delete-user",
      method: "POST",
      data: {
        email,
        companyName: "",
        firstName: name,
        adminName: currentUserName
      }
    }).catch((err) =>
      console.error("delete-user notification email failed", err)
    );

    return updateCollectionDoc("employees", id, {
      currentLinkedIds: remainingIds,
      links: links.filter(({ id: linkId }) => linkId !== companyId)
    });
  };

  const showPendingRequests = isAdmin && pendingRequests.length > 0;

  return (
    <>
      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        vendorId={companyId}
        vendorName={vendorName}
        inviterName={currentUserName}
      />
      <Grid container direction="column" spacing={2}>
        {showPendingRequests && (
          <Grid item>
            <Card
              isLoading={joinRequestsLoading}
              title="Pending Access Requests"
              subtitle="Review requests from buyers to add new users to your account"
              icon={<GroupAddIcon />}
              action={
                <Chip
                  size="small"
                  label={pendingRequests.length}
                  color="warning"
                />
              }
            >
              <Grid
                container
                direction="column"
                spacing={1}
                sx={{ px: 2, py: 1 }}
              >
                {pendingRequests.map((request) => (
                  <Grid item key={request.id}>
                    <JoinRequestRow
                      request={request}
                      onResolve={onResolve}
                      companyId={companyId}
                      adminUid={adminUid}
                    />
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        )}
        <Grid item>
          <Card
            isLoading={employeesLoading}
            title="Team Members"
            subtitle="Manage who has access to your Explorer account and what they can do"
            icon={<AccountCircleIcon />}
            action={
              isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setInviteOpen(true)}
                >
                  Invite
                </Button>
              )
            }
          >
            {!isAdmin && (
              <Box px={2} pt={1}>
                <Typography variant="body2" color="textSecondary">
                  Only admins can manage team members.
                </Typography>
              </Box>
            )}
            <UserManagementPanel
              employees={employees}
              currentUser={currentUser}
              isCurrentUserAdmin={isAdmin}
              onRoleChange={onRoleChange}
              onRemove={onRemove}
            />
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

const PurchasesEmptyState = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    gap={1.5}
    p={3}
  >
    <FontAwesomeIcon icon={faLeaf} size="2x" />
    <Typography variant="subtitle1">No offset purchases yet</Typography>
    <Typography variant="body2" color="textSecondary" align="center">
      Once you buy offsets they&apos;ll appear here with your receipts.
    </Typography>
    <Link href="/explorer/dashboard/offsets">
      <Button variant="contained" color="secondary">
        Browse offset portfolios
      </Button>
    </Link>
  </Box>
);

const PurchaseRow = ({ offset, accountId }) => {
  const {
    id,
    portfolioName = "Carbon offset purchase",
    portfolioImage,
    tonsCo2e,
    purchaseTons,
    totalPrice = 0,
    date,
    hostedInvoiceUrl
  } = offset;

  const tons = tonsCo2e ?? purchaseTons ?? 0;

  const onReceiptClick = () =>
    analyticsTrack("settings_receipt_clicked", {
      accountId,
      offsetId: id,
      portfolioName
    });

  return (
    <TableRow>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          {portfolioImage && (
            <img
              src={portfolioImage}
              alt={portfolioName}
              width={32}
              height={32}
              style={{ borderRadius: 4 }}
            />
          )}
          <Typography variant="body2">{portfolioName}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatDecimal(Number(tons) || 0)} t CO₂e
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{date ? formatDate(date) : ""}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatDollars(Number(totalPrice) || 0)}
        </Typography>
      </TableCell>
      <TableCell>
        {hostedInvoiceUrl ? (
          <Link href={hostedInvoiceUrl} onClick={onReceiptClick}>
            <Typography variant="body2" color="primary">
              View receipt
            </Typography>
          </Link>
        ) : (
          <Typography variant="caption" color="textSecondary">
            No receipt
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const PurchasesTab = ({
  offsets,
  offsetsLoading,
  accountId,
  sortedOffsets
}) => {
  if (offsetsLoading) {
    return <LoadingPanel />;
  }

  return (
    <Card
      title="Past Offset Purchases"
      subtitle="Every carbon-offset purchase your account has made."
      icon={<FontAwesomeIcon icon={faReceipt} />}
    >
      <Box p={2}>
        {offsets.length === 0 ? (
          <PurchasesEmptyState />
        ) : (
          <Box maxHeight={480} overflow="auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Portfolio</TableCell>
                  <TableCell>Tons</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedOffsets.map((offset) => (
                  <PurchaseRow
                    key={offset.id}
                    offset={offset}
                    accountId={accountId}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Card>
  );
};

const Settings = () => {
  const accountId = getAccountId();
  const [user] = useAuth();
  const { displayName: currentUserName, uid: userUid } = user || {};
  const { isAdmin } = usePermissions();

  const [accountData, isAccountDataLoading] = useAccountData();
  const {
    sustainabilityGoals = "",
    historicalDocuments = [],
    name: vendorName,
    vendorSurveys = []
  } = accountData || {};
  const { allEmissions = [] } = useEmissionsContext() || {};

  const [offsets = [], offsetsLoading] = useCachedDisplayData("offsets");
  const [employees = [], employeesLoading] =
    useCollectionDataListener("employees");
  const [joinRequests = [], joinRequestsLoading] = useCachedDisplayData(
    "join-requests",
    [["status", "==", "pending"]]
  );

  const { updateAccountData, updateCollectionDoc } = useCachedFirebaseCrud();

  const location = useLocation();
  const history = useHistory();

  const tabParam = parseTabParam(location.search);
  const activeTab = isValidSettingsTab(tabParam)
    ? tabParam
    : DEFAULT_SETTINGS_TAB;

  useEffect(() => {
    if (isValidSettingsTab(tabParam)) {
      return;
    }
    history.replace({
      pathname: "/explorer/dashboard/settings",
      search: `?tab=${DEFAULT_SETTINGS_TAB}`
    });
  }, [tabParam, history]);

  const [hasTrackedView, setHasTrackedView] = useState(false);

  useEffect(() => {
    if (hasTrackedView) {
      return;
    }
    if (!accountId) {
      return;
    }
    analyticsTrack("settings_page_viewed", {
      accountId,
      initialTab: activeTab
    });
    setHasTrackedView(true);
  }, [hasTrackedView, accountId, activeTab]);

  const sortedOffsets = useMemo(
    () => [...offsets].sort((a, b) => toMillis(b.date) - toMillis(a.date)),
    [offsets]
  );

  const mostRecentSurveyYear = useMemo(() => {
    if (!vendorSurveys.length) {
      return null;
    }
    return Math.max(...vendorSurveys.map(({ year }) => Number(year)));
  }, [vendorSurveys]);

  const totalSurveyTons = useMemo(() => {
    if (!mostRecentSurveyYear) {
      return null;
    }
    const yearEmissions = filterEmissionsForYear(
      allEmissions,
      mostRecentSurveyYear
    );
    return sumTons(
      yearEmissions.filter(({ source }) => source === "vendor-survey")
    );
  }, [allEmissions, mostRecentSurveyYear]);

  const surfaceContext = useMemo(
    () => ({
      companyName: vendorName,
      sustainabilityGoals,
      hasSustainabilityGoals: !!sustainabilityGoals,
      historicalDocumentCount: historicalDocuments.length,
      teamSize: employees.length,
      offsetPurchaseCount: offsets.length,
      mostRecentOffsetPurchaseDate: sortedOffsets[0]?.date ?? null,
      totalSurveyTons,
      mostRecentSurveyYear,
      activeTab
    }),
    [
      vendorName,
      sustainabilityGoals,
      historicalDocuments.length,
      employees.length,
      offsets.length,
      sortedOffsets,
      totalSurveyTons,
      mostRecentSurveyYear,
      activeTab
    ]
  );

  const tabPanels = [
    {
      slug: "goals",
      content: (
        <GoalsTab
          sustainabilityGoals={sustainabilityGoals}
          isAccountDataLoading={isAccountDataLoading}
          updateAccountData={updateAccountData}
          accountId={accountId}
          isAdmin={isAdmin}
        />
      )
    },
    {
      slug: "documents",
      content: (
        <DocumentsTab
          historicalDocuments={historicalDocuments}
          isAccountDataLoading={isAccountDataLoading}
          updateAccountData={updateAccountData}
          accountId={accountId}
          userUid={userUid}
        />
      )
    },
    {
      slug: "team",
      content: (
        <TeamTab
          isAdmin={isAdmin}
          companyId={accountId}
          vendorName={vendorName}
          currentUser={user}
          currentUserName={currentUserName}
          employees={employees}
          employeesLoading={employeesLoading}
          joinRequests={joinRequests}
          joinRequestsLoading={joinRequestsLoading}
          updateCollectionDoc={updateCollectionDoc}
        />
      )
    },
    {
      slug: "purchases",
      content: (
        <PurchasesTab
          offsets={offsets}
          offsetsLoading={offsetsLoading}
          accountId={accountId}
          sortedOffsets={sortedOffsets}
        />
      )
    }
  ];

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <SettingsTabs activeTab={activeTab} />
        {tabPanels.map(({ slug, content }) => (
          <Box
            key={slug}
            sx={{ display: slug === activeTab ? "block" : "none" }}
          >
            {content}
          </Box>
        ))}
      </Box>
      <ScoutFab surface="settings" surfaceContext={surfaceContext} />
    </>
  );
};

export default Settings;
